import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Package,
  Video,
  Instagram,
  Music,
  FileText,
  CheckCircle2,
  Circle,
  Edit,
  ChevronDown,
  ChevronUp,
  Link
} from 'lucide-react';
import { format, isPast, isToday, isTomorrow, differenceInDays, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { de } from 'date-fns/locale';
import { EditProjectModal } from './EditProjectModal';
import { fetchCampaignMetrics, fetchLexofficeInvoice, updateMetaCampaignBudget, searchAvailableReports, type CampaignMetrics, type LexofficeInvoiceData, type ReportSearchResult } from '../lib/reportingSupabase';
import { supabase } from '../lib/supabase';
import { logModalDecision } from '../lib/modalLog';
import toast from 'react-hot-toast';

interface ProjectCardProps {
  project: any;
  onUpdate: () => void;
  onDragStart?: (projectId: string) => void;
  onDragEnd?: () => void;
  onMetricsLoaded?: (projectId: string, spend: number, budget: number, platformStatus?: { meta: string; tiktok: string }) => void;
  onCardDrop?: (sourceProjectId: string, targetProjectId: string) => void;
  onCheckPostedVideos?: (artistId: string, artistName: string) => void;
  dashboardRole?: 'default' | 'content' | 'ads';
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onUpdate, onDragStart, onDragEnd, onMetricsLoaded, onCardDrop, onCheckPostedVideos, dashboardRole = 'default' }) => {
  const isContentView = dashboardRole === 'content';
  const isAdsView = dashboardRole === 'ads';
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  // Default collapsed state for completed, active_campaign, and ad_only projects
  // In ads view, ad_only pre-release cards are expanded so they show budget + release info
  const [isExpanded, setIsExpanded] = useState(
    project.project_status !== 'completed' && project.project_status !== 'active_campaign' && (project.service_package !== 'ad_only' || dashboardRole === 'ads')
  );
  const [campaignMetrics, setCampaignMetrics] = useState<CampaignMetrics | null>(null);
  const [lexofficeData, setLexofficeData] = useState<LexofficeInvoiceData | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isShiftingBudget, setIsShiftingBudget] = useState(false);
  const [isCheckingMetaAccess, setIsCheckingMetaAccess] = useState(false);
  const [showPackageMenu, setShowPackageMenu] = useState(false);
  const [showOverspendModal, setShowOverspendModal] = useState(false);
  const [newBudgetInput, setNewBudgetInput] = useState('');
  const [showReportSuggestModal, setShowReportSuggestModal] = useState(false);
  const [suggestedReports, setSuggestedReports] = useState<ReportSearchResult[]>([]);
  const [linkingReportId, setLinkingReportId] = useState<string | null>(null);

  // Fetch campaign metrics for projects with budgets
  // For completed projects: auto-set budget to actual spend (no modal needed)
  useEffect(() => {
    const dbgTag = `%c[MetricsFetch] project=${project.id} (${project.artist_name} - ${project.release_name})`;
    const dbgStyle = 'color: #6366f1; font-weight: bold';
    const shouldFetch = !isContentView && ['active_campaign', 'completed', 'follow_up'].includes(project.project_status) && project.release_name;
    console.log(`${dbgTag} useEffect fired`, dbgStyle, {
      project_status: project.project_status,
      release_name: project.release_name,
      shouldFetch,
      linked_report_ids: project.linked_report_ids,
    });

    if (shouldFetch) {
      fetchCampaignMetrics(project.release_name, project.artist_name, project.id, project.linked_report_ids || []).then(async metrics => {
        console.log(`${dbgTag} fetchCampaignMetrics result`, dbgStyle, {
          hasMetrics: !!metrics,
          metrics: metrics ? { spend: metrics.spend, budget: metrics.budget } : null,
        });
        if (metrics) {
          setCampaignMetrics(metrics);
          const budget = project.ad_budget ? project.ad_budget / 100 : 0;
          onMetricsLoaded?.(project.id, metrics.spend, budget, {
            meta: metrics.meta?.status || 'unknown',
            tiktok: metrics.tiktok?.status || 'unknown',
          });

          // For completed projects: auto-sync budget to actual spend if they differ
          if (project.project_status === 'completed' && metrics.spend > 0) {
            const currentBudgetEuros = project.ad_budget ? project.ad_budget / 100 : 0;
            console.log(`${dbgTag} completed project auto-sync check`, dbgStyle, {
              currentBudgetEuros,
              actualSpend: metrics.spend,
              diff: Math.abs(currentBudgetEuros - metrics.spend),
              shouldSync: Math.abs(currentBudgetEuros - metrics.spend) > 1,
            });
            if (Math.abs(currentBudgetEuros - metrics.spend) > 1) {
              try {
                const { error } = await supabase
                  .from('project_tracking')
                  .update({ ad_budget: Math.round(metrics.spend * 100) } as any)
                  .eq('id', project.id);
                if (!error) {
                  console.log(`${dbgTag} ✅ Auto-set budget for completed project: ${metrics.spend.toFixed(2)}€`, dbgStyle);
                  onUpdate();
                } else {
                  console.error(`${dbgTag} ❌ auto-set budget DB error`, dbgStyle, { error });
                }
              } catch (e) {
                console.error(`${dbgTag} ❌ auto-set budget exception`, dbgStyle, { error: e });
              }
            }
          }
        } else {
          console.log(`${dbgTag} no metrics returned`, dbgStyle);
        }
      });
    } else {
      console.log(`${dbgTag} → SKIP: not active_campaign/completed/follow_up or no release_name`, dbgStyle);
    }
  }, [project.project_status, project.release_name, project.artist_name, project.id, JSON.stringify(project.linked_report_ids)]);

  // Fetch Lexoffice invoice data for projects with a lexoffice_invoice_id
  // This is used for the 3-way reconciliation: actual spend vs Monday.com budget vs Lexoffice invoiced
  useEffect(() => {
    const dbgTag = `%c[LexofficeFetch] project=${project.id} (${project.artist_name} - ${project.release_name})`;
    const dbgStyle = 'color: #ec4899; font-weight: bold';
    const invoiceId = project.lexoffice_invoice_id;
    console.log(`${dbgTag} useEffect fired`, dbgStyle, {
      lexoffice_invoice_id: invoiceId,
      project_status: project.project_status,
    });

    if (!invoiceId) {
      console.log(`${dbgTag} → SKIP: no lexoffice_invoice_id`, dbgStyle);
      setLexofficeData(null);
      return;
    }

    fetchLexofficeInvoice(invoiceId).then(data => {
      console.log(`${dbgTag} fetchLexofficeInvoice result`, dbgStyle, {
        hasData: !!data,
        media_budget_net: data?.media_budget_net,
        voucher_number: data?.voucher_number,
        voucher_status: data?.voucher_status,
      });
      if (data) {
        setLexofficeData(data);
      } else {
        setLexofficeData(null);
      }
    });
  }, [project.lexoffice_invoice_id, project.id, project.artist_name, project.release_name]);

  // Show overspend modal when spend exceeds budget by more than 1%
  // Uses Meta budget (campaignMetrics.budget) if available, falls back to project.ad_budget
  // Skipped for completed projects (budget is auto-synced above)
  // Acknowledgment is stored in the database (budget_ack_spend, budget_ack_budget) so
  // it persists across browsers/devices. The modal only reappears if:
  //   - spend has grown by >10% since last acknowledgment, OR
  //   - the project budget has changed by >1€ since last acknowledgment
  useEffect(() => {
    if (isContentView) return; // No budget modals in content view
    const dbgTag = `%c[OverspendModal] project=${project.id} (${project.artist_name} - ${project.release_name})`;
    const dbgStyle = 'color: #f59e0b; font-weight: bold';

    console.log(`${dbgTag} useEffect fired`, dbgStyle, {
      project_id: project.id,
      artist: project.artist_name,
      release: project.release_name,
      project_status: project.project_status,
      ad_budget_cents: project.ad_budget,
      ad_budget_euros: project.ad_budget ? project.ad_budget / 100 : 0,
      budget_ack_spend: project.budget_ack_spend,
      budget_ack_budget: project.budget_ack_budget,
      lexoffice_ack_amount: project.lexoffice_ack_amount,
      campaignMetrics: campaignMetrics ? {
        spend: campaignMetrics.spend,
        budget_cents: campaignMetrics.budget,
        budget_euros: campaignMetrics.budget ? campaignMetrics.budget / 100 : 0,
      } : null,
      lexofficeData: lexofficeData ? {
        media_budget_net: lexofficeData.media_budget_net,
        voucher_number: lexofficeData.voucher_number,
        voucher_status: lexofficeData.voucher_status,
      } : null,
    });

    if (!campaignMetrics) {
      console.log(`${dbgTag} → HIDE: no campaignMetrics`, dbgStyle);
      setShowOverspendModal(false);
      return;
    }
    if (project.project_status === 'completed') {
      console.log(`${dbgTag} → HIDE: project is completed`, dbgStyle);
      setShowOverspendModal(false);
      return;
    }

    const metaBudgetEuros = campaignMetrics.meta?.budget || 0;
    const tiktokBudgetEuros = campaignMetrics.tiktok?.budget || 0;
    const metaSpend = campaignMetrics.meta?.spend || 0;
    const tiktokSpend = campaignMetrics.tiktok?.spend || 0;
    const projectBudgetEuros = project.ad_budget ? project.ad_budget / 100 : 0;
    const totalPlatformBudget = metaBudgetEuros + tiktokBudgetEuros;
    const effectiveBudget = totalPlatformBudget > 0 ? totalPlatformBudget : projectBudgetEuros;
    if (effectiveBudget <= 0) {
      console.log(`${dbgTag} → HIDE: effectiveBudget <= 0`, dbgStyle, { metaBudgetEuros, projectBudgetEuros, effectiveBudget });
      setShowOverspendModal(false);
      return;
    }

    // Determine platform statuses and end dates
    const metaStatus = campaignMetrics.meta?.status || 'unknown';
    const tiktokStatus = campaignMetrics.tiktok?.status || 'unknown';
    const metaEndDate = campaignMetrics.meta?.end_date ? new Date(campaignMetrics.meta.end_date) : null;
    const tiktokEndDate = campaignMetrics.tiktok?.end_date ? new Date(campaignMetrics.tiktok.end_date) : null;
    const now = new Date();

    // TRIGGER 1: Overspend — total spend exceeds total budget by >1%
    // This applies regardless of campaign status (active or ended)
    const overspend = campaignMetrics.spend > effectiveBudget * 1.01;

    // TRIGGER 2: Shift opportunity — both platforms ended/inactive AND there's remaining budget
    // Only triggers if BOTH platforms are not active (ended/paused) and total spend < total budget
    const bothPlatformsInactive = (metaStatus !== 'active' || campaignMetrics.meta == null || campaignMetrics.meta.campaign_count === 0)
      && (tiktokStatus !== 'active' || campaignMetrics.tiktok == null || campaignMetrics.tiktok.campaign_count === 0);
    const hasRemainingBudget = campaignMetrics.spend < effectiveBudget * 0.99;
    const shiftOpportunity = bothPlatformsInactive && hasRemainingBudget;

    // TRIGGER 3: Overspend on a single platform before its end date
    // (e.g. Meta spent more than its budget while still active)
    const metaOverspend = metaStatus === 'active' && metaBudgetEuros > 0 && metaSpend > metaBudgetEuros * 1.01;
    const tiktokOverspend = tiktokStatus === 'active' && tiktokBudgetEuros > 0 && tiktokSpend > tiktokBudgetEuros * 1.01;

    // Lexoffice mismatch (still check, but only if we have data)
    const lexofficeMismatch = lexofficeData != null
      && lexofficeData.media_budget_net > 0
      && effectiveBudget > 0
      && Math.abs(lexofficeData.media_budget_net - effectiveBudget) > 1;

    console.log(`${dbgTag} trigger check`, dbgStyle, {
      spend: campaignMetrics.spend,
      metaBudgetEuros, tiktokBudgetEuros, totalPlatformBudget,
      projectBudgetEuros, effectiveBudget,
      metaStatus, tiktokStatus,
      metaEndDate, tiktokEndDate,
      overspend, shiftOpportunity, metaOverspend, tiktokOverspend, lexofficeMismatch,
    });

    // Only show modal for real issues:
    // - Overspend (total or per-platform while active)
    // - Shift opportunity (both platforms ended with remaining budget)
    // - Lexoffice mismatch
    if (!overspend && !shiftOpportunity && !metaOverspend && !tiktokOverspend && !lexofficeMismatch) {
      console.log(`${dbgTag} → HIDE: no overspend, no shift opportunity, no lexoffice mismatch`, dbgStyle);
      setShowOverspendModal(false);
      return;
    }

    // Check database-backed acknowledgment
    const ackedSpend = parseFloat(project.budget_ack_spend || '0');
    const ackedBudget = parseFloat(project.budget_ack_budget || '0');
    const ackedLexoffice = parseFloat(project.lexoffice_ack_amount || '0');

    // "Do not ask again on same day" — if the user dismissed the modal today,
    // suppress it entirely regardless of spend/budget changes
    const todayStr = new Date().toISOString().split('T')[0];
    const ackedToday = project.budget_ack_date === todayStr;
    if (ackedToday) {
      console.log(`${dbgTag} → HIDE: already dismissed today (${todayStr})`, dbgStyle);
      setShowOverspendModal(false);
      return;
    }

    // Suppress if spend hasn't grown by >10% AND budget hasn't changed by >1€
    // AND Lexoffice amount hasn't changed by >1€ since last ack
    // For shift opportunities: suppress if both platforms were already inactive last time
    const spendUnchanged = campaignMetrics.spend <= ackedSpend * 1.10;
    const budgetUnchanged = Math.abs(effectiveBudget - ackedBudget) < 1;
    const lexofficeUnchanged = lexofficeData != null
      ? Math.abs(lexofficeData.media_budget_net - ackedLexoffice) < 1
      : true;
    // For shift opportunities: if the user already acknowledged and spend hasn't changed,
    // don't re-trigger (the remaining budget is the same)
    const shiftAlreadyAcked = shiftOpportunity && spendUnchanged;
    console.log(`${dbgTag} ack check`, dbgStyle, {
      ackedSpend,
      ackedBudget,
      ackedLexoffice,
      ackedDate: project.budget_ack_date,
      ackedToday,
      currentSpend: campaignMetrics.spend,
      currentBudget: effectiveBudget,
      currentLexoffice: lexofficeData?.media_budget_net,
      spendUnchanged,
      budgetUnchanged,
      lexofficeUnchanged,
      shiftAlreadyAcked,
      spendGrowthPct: ackedSpend > 0 ? ((campaignMetrics.spend - ackedSpend) / ackedSpend * 100).toFixed(1) + '%' : 'N/A (no prior ack)',
      budgetDiff: Math.abs(effectiveBudget - ackedBudget).toFixed(2),
      lexofficeDiff: lexofficeData ? Math.abs(lexofficeData.media_budget_net - ackedLexoffice).toFixed(2) : 'N/A',
    });
    if ((spendUnchanged && budgetUnchanged && lexofficeUnchanged) || shiftAlreadyAcked) {
      console.log(`${dbgTag} → HIDE: acknowledged (spend + budget + lexoffice unchanged)`, dbgStyle);
      setShowOverspendModal(false);
      return;
    }

    // Pre-fill with ceil(spend) — the input is only shown for real overspend
    // (spend > project budget), so the natural new budget is the actual spend
    const prefill = Math.ceil(campaignMetrics.spend).toString();
    console.log(`%c${dbgTag} → ✅ SHOW MODAL`, 'color: #ef4444; font-weight: bold', {
      prefill,
      overspend,
      shiftOpportunity,
      metaOverspend,
      tiktokOverspend,
      lexofficeMismatch,
      spendUnchanged,
      budgetUnchanged,
      lexofficeUnchanged,
      reason: !spendUnchanged && !budgetUnchanged ? 'both spend and budget changed'
        : !spendUnchanged ? 'spend grew >10% since last ack'
        : !budgetUnchanged ? 'budget changed >1€ since last ack'
        : !lexofficeUnchanged ? 'lexoffice amount changed >1€ since last ack'
        : 'unknown',
    });
    setNewBudgetInput(prefill);
    setShowOverspendModal(true);
  }, [campaignMetrics, lexofficeData, project.ad_budget, project.id, project.project_status, project.budget_ack_spend, project.budget_ack_budget, project.lexoffice_ack_amount]);

  const acknowledgeOverspend = async () => {
    const dbgTag = `%c[OverspendModal] acknowledgeOverspend project=${project.id}`;
    const dbgStyle = 'color: #3b82f6; font-weight: bold';
    console.log(`${dbgTag} START`, dbgStyle, {
      project_id: project.id,
      artist: project.artist_name,
      release: project.release_name,
      hasCampaignMetrics: !!campaignMetrics,
    });

    if (campaignMetrics) {
      const metaBudgetEuros = campaignMetrics.meta?.budget || 0;
      const tiktokBudgetEuros = campaignMetrics.tiktok?.budget || 0;
      const projectBudgetEuros = project.ad_budget ? project.ad_budget / 100 : 0;
      const totalPlatformBudget = metaBudgetEuros + tiktokBudgetEuros;
      const effectiveBudget = totalPlatformBudget > 0 ? totalPlatformBudget : projectBudgetEuros;
      const lexofficeMediaBudget = lexofficeData?.media_budget_net ?? 0;
      console.log(`${dbgTag} saving ack to DB`, dbgStyle, {
        budget_ack_spend: campaignMetrics.spend,
        budget_ack_budget: effectiveBudget,
        lexoffice_ack_amount: lexofficeMediaBudget,
      });
      // Save acknowledgment to database (persists across browsers/devices)
      const { error } = await supabase
        .from('project_tracking')
        .update({
          budget_ack_spend: campaignMetrics.spend,
          budget_ack_budget: effectiveBudget,
          budget_ack_date: new Date().toISOString().split('T')[0],
          lexoffice_ack_amount: lexofficeMediaBudget,
        } as any)
        .eq('id', project.id);

      if (error) {
        console.error(`${dbgTag} ❌ DB UPDATE ERROR`, dbgStyle, { error });
      } else {
        console.log(`${dbgTag} ✅ DB UPDATE SUCCESS`, dbgStyle);
      }

      // Log the decision
      console.log(`${dbgTag} logging to modal_decision_log`, dbgStyle, {
        spend: campaignMetrics.spend,
        meta_budget: metaBudgetEuros,
        project_budget: projectBudgetEuros,
        lexoffice_media_budget: lexofficeMediaBudget,
      });
      await logModalDecision(project.id, 'overspend_budget', 'acknowledged', {
        spend: campaignMetrics.spend,
        meta_budget: metaBudgetEuros,
        project_budget: projectBudgetEuros,
        lexoffice_media_budget: lexofficeMediaBudget,
        ack_spend: campaignMetrics.spend,
        ack_budget: projectBudgetEuros,
        ack_lexoffice: lexofficeMediaBudget,
      });
    } else {
      console.warn(`${dbgTag} no campaignMetrics — skipping DB update`, dbgStyle);
    }
    console.log(`${dbgTag} → hiding modal`, dbgStyle);
    setShowOverspendModal(false);
  };

  const handleSaveNewBudget = async () => {
    const dbgTag = `%c[OverspendModal] handleSaveNewBudget project=${project.id}`;
    const dbgStyle = 'color: #8b5cf6; font-weight: bold';
    const value = parseFloat(newBudgetInput);
    console.log(`${dbgTag} START`, dbgStyle, {
      project_id: project.id,
      artist: project.artist_name,
      release: project.release_name,
      rawInput: newBudgetInput,
      parsedValue: value,
      isValid: !isNaN(value) && value > 0,
    });

    if (isNaN(value) || value <= 0) {
      console.warn(`${dbgTag} ❌ invalid budget value`, dbgStyle, { value, rawInput: newBudgetInput });
      toast.error('Bitte gültiges Budget eingeben');
      return;
    }
    const budgetCents = Math.round(value * 100);
    const oldBudgetEuros = project.ad_budget ? project.ad_budget / 100 : 0;
    // Use effective budget (platform budgets sum) for acknowledgment
    const metaB = campaignMetrics?.meta?.budget || 0;
    const tiktokB = campaignMetrics?.tiktok?.budget || 0;
    const totalPlatformB = metaB + tiktokB;
    const ackBudget = totalPlatformB > 0 ? totalPlatformB : value;
    console.log(`${dbgTag} saving to DB`, dbgStyle, {
      budgetCents,
      budgetEuros: value,
      oldBudgetEuros,
      ack_spend: campaignMetrics?.spend || 0,
      ack_budget: ackBudget,
      ack_lexoffice: lexofficeData?.media_budget_net || 0,
    });
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('project_tracking')
        .update({
          ad_budget: budgetCents,
          // Acknowledge with the effective budget value and current spend so the modal
          // doesn't re-trigger immediately after saving
          budget_ack_spend: campaignMetrics?.spend || 0,
          budget_ack_budget: ackBudget,
          budget_ack_date: new Date().toISOString().split('T')[0],
          lexoffice_ack_amount: lexofficeData?.media_budget_net || 0,
        } as any)
        .eq('id', project.id);

      if (error) {
        console.error(`${dbgTag} ❌ DB UPDATE ERROR`, dbgStyle, { error });
        throw error;
      }
      console.log(`${dbgTag} ✅ DB UPDATE SUCCESS`, dbgStyle);

      // Log the decision
      console.log(`${dbgTag} logging to modal_decision_log`, dbgStyle, {
        old_budget: oldBudgetEuros,
        new_budget: value,
        spend: campaignMetrics?.spend || 0,
      });
      await logModalDecision(project.id, 'overspend_budget', 'saved_new_budget', {
        spend: campaignMetrics?.spend || 0,
        meta_budget: campaignMetrics?.budget ? campaignMetrics.budget / 100 : 0,
        old_budget: oldBudgetEuros,
        new_budget: value,
        ack_spend: campaignMetrics?.spend || 0,
        ack_budget: value,
      });

      toast.success('Budget aktualisiert');
      console.log(`${dbgTag} → hiding modal, calling onUpdate()`, dbgStyle);
      setShowOverspendModal(false);
      onUpdate();
    } catch (error) {
      console.error(`${dbgTag} ❌ EXCEPTION`, dbgStyle, { error });
      toast.error('Fehler beim Aktualisieren des Budgets');
    } finally {
      setIsUpdating(false);
    }
  };

  // Auto-check for available reports on load (onboarding & upcoming cards without linked reports)
  useEffect(() => {
    const dbgTag = `%c[ReportSuggestModal] useEffect project=${project.id} (${project.artist_name} - ${project.release_name})`;
    const dbgStyle = 'color: #06b6d4; font-weight: bold';
    console.log(`${dbgTag} fired`, dbgStyle, {
      project_id: project.id,
      project_status: project.project_status,
      release_date: project.release_date,
      linked_report_ids: project.linked_report_ids,
      report_suggestion_dismissed: project.report_suggestion_dismissed,
      dismissed_report_ids: project.dismissed_report_ids,
    });

    const hasLinkedReports = (project.linked_report_ids || []).length > 0;
    if (hasLinkedReports) {
      console.log(`${dbgTag} → SKIP: project already has linked reports`, dbgStyle, { linked_report_ids: project.linked_report_ids });
      return;
    }

    // Only suggest reports if release date is in the PAST (strictly before today)
    // Don't prompt for upcoming releases or releases happening today
    const isReleased = project.release_date && new Date(project.release_date) < new Date(new Date().toDateString());
    if (!isReleased) {
      console.log(`${dbgTag} → SKIP: release date not in the past`, dbgStyle, { isReleased, releaseDate: project.release_date, projectStatus: project.project_status });
      return;
    }

    // Don't re-prompt if the user already dismissed the suggestion for this project
    // (stored in database so it persists across browsers/devices)
    if (project.report_suggestion_dismissed) {
      console.log(`${dbgTag} → SKIP: report_suggestion_dismissed = true`, dbgStyle);
      return;
    }

    const dismissedIds = new Set(project.dismissed_report_ids || []);
    console.log(`${dbgTag} searching for available reports...`, dbgStyle, {
      artist: project.artist_name,
      release: project.release_name,
      dismissedIds: [...dismissedIds],
    });

    let cancelled = false;
    searchAvailableReports(project.artist_name, project.release_name).then(reports => {
      if (cancelled) {
        console.log(`${dbgTag} → cancelled (component unmounted)`, dbgStyle);
        return;
      }
      console.log(`${dbgTag} search results`, dbgStyle, {
        totalFound: reports.length,
        allReports: reports.map(r => ({ id: r.id, name: r.name })),
      });
      // Filter out reports the user has already dismissed
      const filtered = reports.filter(r => !dismissedIds.has(r.id));
      console.log(`${dbgTag} after filtering dismissed`, dbgStyle, {
        beforeFilter: reports.length,
        afterFilter: filtered.length,
        filteredOut: reports.filter(r => dismissedIds.has(r.id)).map(r => ({ id: r.id, name: r.name })),
        remaining: filtered.map(r => ({ id: r.id, name: r.name })),
      });
      if (filtered.length === 0) {
        console.log(`${dbgTag} → SKIP: no reports left after filtering`, dbgStyle);
        return;
      }
      console.log(`%c${dbgTag} → ✅ SHOW MODAL`, 'color: #06b6d4; font-weight: bold', {
        suggestedReports: filtered.map(r => ({ id: r.id, name: r.name })),
      });
      setSuggestedReports(filtered);
      setShowReportSuggestModal(true);
    });

    return () => { cancelled = true; };
  }, [project.id, project.project_status, project.release_date, project.artist_name, project.release_name, JSON.stringify(project.linked_report_ids), project.report_suggestion_dismissed, JSON.stringify(project.dismissed_report_ids)]);

  const dismissReportSuggestion = async () => {
    const dbgTag = `%c[ReportSuggestModal] dismissReportSuggestion (NICHT VERLINKEN) project=${project.id}`;
    const dbgStyle = 'color: #ec4899; font-weight: bold';
    console.log(`${dbgTag} START`, dbgStyle, {
      project_id: project.id,
      artist: project.artist_name,
      release: project.release_name,
      suggestedReports: suggestedReports.map(r => ({ id: r.id, name: r.name })),
    });

    // Save the dismissed report IDs so we don't suggest them again,
    // and mark the suggestion as dismissed for this project.
    const dismissedIds = suggestedReports.map(r => r.id);
    const existingDismissed = project.dismissed_report_ids || [];
    const mergedDismissed = [...new Set([...existingDismissed, ...dismissedIds])];
    console.log(`${dbgTag} merging dismissed IDs`, dbgStyle, {
      newDismissals: dismissedIds,
      existingDismissed,
      mergedDismissed,
    });

    const { error } = await supabase
      .from('project_tracking')
      .update({
        report_suggestion_dismissed: true,
        dismissed_report_ids: mergedDismissed,
      } as any)
      .eq('id', project.id);

    if (error) {
      console.error(`${dbgTag} ❌ DB UPDATE ERROR`, dbgStyle, { error });
    } else {
      console.log(`${dbgTag} ✅ DB UPDATE SUCCESS`, dbgStyle, {
        report_suggestion_dismissed: true,
        dismissed_report_ids: mergedDismissed,
      });
    }

    // Log the decision
    console.log(`${dbgTag} logging to modal_decision_log`, dbgStyle);
    await logModalDecision(project.id, 'report_suggestion', 'dismissed', {
      report_ids_shown: dismissedIds,
      report_names_shown: suggestedReports.map(r => r.name),
      dismissed_report_ids: mergedDismissed,
    });

    console.log(`${dbgTag} → hiding modal`, dbgStyle);
    setShowReportSuggestModal(false);
  };

  const handleLinkSuggestedReport = async (report: ReportSearchResult) => {
    const dbgTag = `%c[ReportSuggestModal] handleLinkSuggestedReport project=${project.id}`;
    const dbgStyle = 'color: #10b981; font-weight: bold';
    console.log(`${dbgTag} START`, dbgStyle, {
      project_id: project.id,
      artist: project.artist_name,
      release: project.release_name,
      reportToLink: { id: report.id, name: report.name },
      currentLinkedIds: project.linked_report_ids,
      allSuggestedReports: suggestedReports.map(r => ({ id: r.id, name: r.name })),
    });
    setLinkingReportId(report.id);
    try {
      const newIds = [...(project.linked_report_ids || []), report.id];
      console.log(`${dbgTag} updating linked_report_ids in DB`, dbgStyle, {
        oldIds: project.linked_report_ids || [],
        newIds,
      });
      const { error } = await supabase
        .from('project_tracking')
        .update({ linked_report_ids: newIds } as any)
        .eq('id', project.id);

      if (error) {
        console.error(`${dbgTag} ❌ DB UPDATE ERROR`, dbgStyle, { error });
        throw error;
      }
      console.log(`${dbgTag} ✅ DB UPDATE SUCCESS`, dbgStyle);

      // Invalidate metrics cache so the linked report is picked up immediately
      console.log(`${dbgTag} invalidating campaign_metrics_cache`, dbgStyle);
      await supabase
        .from('campaign_metrics_cache')
        .delete()
        .eq('project_id', project.id);

      // Log the decision
      console.log(`${dbgTag} logging to modal_decision_log`, dbgStyle);
      await logModalDecision(project.id, 'report_suggestion', 'linked_report', {
        report_id: report.id,
        report_name: report.name,
        report_ids_shown: suggestedReports.map(r => r.id),
        linked_report_ids: newIds,
      });

      toast.success(`Report "${report.name}" verlinkt`);
      console.log(`${dbgTag} → hiding modal, calling onUpdate()`, dbgStyle);
      setShowReportSuggestModal(false);
      onUpdate();
    } catch (error) {
      console.error(`${dbgTag} ❌ EXCEPTION`, dbgStyle, { error });
      toast.error('Fehler beim Verlinken des Reports');
    } finally {
      setLinkingReportId(null);
    }
  };

  // Close package menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showPackageMenu && !target.closest('.package-menu-container')) {
        setShowPackageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPackageMenu]);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('projectId', project.id);
    e.dataTransfer.setData('currentStatus', project.project_status);
    onDragStart?.(project.id);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd?.();
  };

  const handleCardDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleCardDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleCardDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const sourceProjectId = e.dataTransfer.getData('projectId');
    if (sourceProjectId && sourceProjectId !== project.id) {
      onCardDrop?.(sourceProjectId, project.id);
    }
  };

  const getPackageColor = (pkg: string) => {
    switch (pkg) {
      case 'full':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'content_only':
        return 'bg-gray-600/20 text-gray-400 border-gray-600/30';
      case 'ads_only':
        return 'bg-blue-400/20 text-blue-300 border-blue-400/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getPackageLabel = (pkg: string) => {
    switch (pkg) {
      case 'full':
        return 'Full';
      case 'content_only':
        return 'Content Only';
      case 'ad_only':
        return 'Ads Only';
      default:
        return pkg;
    }
  };

  const getOnboardingProgress = () => {
    // For ad_only projects, only count Meta Access
    const steps = (project.service_package === 'ad_only')
      ? [project.meta_access_granted]
      : [
          project.meta_access_granted,
          project.masterclass_login_created,
          project.masterclass_watched,
          project.release_sheet_completed,
          project.first_four_videos_uploaded
        ];
    const completed = steps.filter(Boolean).length;
    return { completed, total: steps.length, percentage: (completed / steps.length) * 100 };
  };

  const getDateColor = (dateStr: string | null) => {
    if (!dateStr) return 'text-gray-500';
    const date = new Date(dateStr);
    const daysUntil = differenceInDays(date, new Date());
    
    if (isPast(date)) return 'text-gray-500';
    if (isToday(date)) return 'text-blue-400';
    if (isTomorrow(date)) return 'text-blue-300';
    if (daysUntil <= 7) return 'text-blue-300';
    return 'text-gray-400';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isToday(date)) return 'Heute';
    if (isTomorrow(date)) return 'Morgen';
    return format(date, 'dd.MM.yyyy', { locale: de });
  };

  const isThisWeekRelease = () => {
    if (!project.release_date) return false;
    const releaseDate = new Date(project.release_date);
    const now = new Date();
    
    // Get start of week (Monday) and end of week (Sunday)
    const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // 1 = Monday
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    
    // Check if release is within this week
    return isWithinInterval(releaseDate, { start: weekStart, end: weekEnd });
  };

  const handleToggleStep = async (field: string, currentValue: boolean) => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    try {
      const updateData: any = {
        [field]: !currentValue
      };
      
      // Also update the corresponding date field
      const dateField = field + '_date';
      updateData[dateField] = !currentValue ? new Date().toISOString() : null;
      
      const { error } = await supabase
        .from('project_tracking')
        .update(updateData)
        .eq('id', project.id);
      
      if (error) throw error;
      
      toast.success('Status aktualisiert');
      onUpdate();
    } catch (error) {
      console.error('Error updating step:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCheckMetaAccess = async () => {
    if (isCheckingMetaAccess) return;
    setIsCheckingMetaAccess(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-meta-access', {
        body: { artist_id: project.artist_id },
      });

      if (error) throw error;

      if (data?.matched && data?.updated > 0) {
        toast.success('Meta Access bestätigt — FB Page / IG Account gefunden!');
        onUpdate();
      } else if (data?.matched && data?.matched > 0) {
        toast.info('Meta Access bereits vorhanden');
      } else {
        toast.warning('Keine passende FB Page / IG Account gefunden');
      }
    } catch (error) {
      console.error('Error checking Meta access:', error);
      toast.error('Fehler beim Prüfen des Meta Access');
    } finally {
      setIsCheckingMetaAccess(false);
    }
  };

  const handlePackageChange = async (newPackage: string) => {
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('releases')
        .update({ service_package: newPackage } as any)
        .eq('id', project.release_id);
      
      if (error) throw error;
      
      toast.success('Paket aktualisiert');
      setShowPackageMenu(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating package:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setIsUpdating(false);
    }
  };

  const progress = getOnboardingProgress();

  return (
    <>
      {showEditModal && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            onUpdate();
            setShowEditModal(false);
          }}
        />
      )}

      {showOverspendModal && campaignMetrics && (() => {
        const projectBudgetEuros = (project.ad_budget || 0) / 100;
        const mondayBudgetEuros = (project.monday_ad_budget || 0) / 100;
        const metaBudgetEuros = campaignMetrics.meta?.budget || 0;
        const metaSpend = campaignMetrics.meta?.spend || 0;
        const tiktokBudgetEuros = campaignMetrics.tiktok?.budget || 0;
        const tiktokSpend = campaignMetrics.tiktok?.spend || 0;
        const spend = campaignMetrics.spend;
        // Budget Tool = sum of all platform planned budgets (Meta + TikTok)
        const totalPlatformBudget = metaBudgetEuros + tiktokBudgetEuros;
        const budgetToolEuros = totalPlatformBudget > 0 ? totalPlatformBudget : projectBudgetEuros;
        const overspendVsProject = spend > budgetToolEuros * 1.01;
        // Mismatch: sum of all platform budgets vs project budget
        const budgetMismatch = totalPlatformBudget > 0 && projectBudgetEuros > 0 && Math.abs(totalPlatformBudget - projectBudgetEuros) > 1;
        const lexofficeMismatch = lexofficeData != null && lexofficeData.media_budget_net > 0 && budgetToolEuros > 0 && Math.abs(lexofficeData.media_budget_net - budgetToolEuros) > 1;
        const mondayDeviation = mondayBudgetEuros > 0 && budgetToolEuros > 0 && Math.abs(mondayBudgetEuros - budgetToolEuros) > 1;

        // TikTok ended with remaining budget → can shift to Meta
        const tiktokEnded = campaignMetrics.tiktok?.status === 'ended';
        const tiktokRemaining = tiktokBudgetEuros - tiktokSpend;
        const canShiftTiktokToMeta = tiktokEnded && tiktokRemaining > 1 && campaignMetrics.meta != null && campaignMetrics.meta.status === 'active' && !!project.meta_campaign_id;
        const newMetaBudgetAfterShift = metaBudgetEuros + tiktokRemaining;

        const formatPlatformDate = (dateStr: string | null) => {
          if (!dateStr) return null;
          try { return format(new Date(dateStr), 'dd.MM.yyyy', { locale: de }); } catch { return null; }
        };
        const metaEndDate = formatPlatformDate(campaignMetrics.meta?.end_date || null);
        const tiktokEndDate = formatPlatformDate(campaignMetrics.tiktok?.end_date || null);

        const handleShiftTiktokToMeta = async () => {
          if (!project.meta_campaign_id) return;
          setIsShiftingBudget(true);
          try {
            const newBudgetCents = Math.round(newMetaBudgetAfterShift * 100);
            const result = await updateMetaCampaignBudget(project.meta_campaign_id, newBudgetCents);
            if (result?.success) {
              // Update project_tracking budget to reflect the new total
              const newProjectBudgetCents = Math.round((newMetaBudgetAfterShift + tiktokSpend) * 100);
              await supabase
                .from('project_tracking')
                .update({
                  ad_budget: newProjectBudgetCents,
                  budget_ack_spend: spend,
                  budget_ack_budget: newMetaBudgetAfterShift + tiktokSpend,
                  budget_ack_date: new Date().toISOString().split('T')[0],
                } as any)
                .eq('id', project.id);

              // Log the shift
              await logModalDecision(project.id, 'budget_shift', 'shifted_tiktok_to_meta', {
                from_platform: 'tiktok',
                to_platform: 'meta',
                meta_campaign_id: project.meta_campaign_id,
                amount_euros: tiktokRemaining,
                old_meta_budget: metaBudgetEuros,
                new_meta_budget: newMetaBudgetAfterShift,
                tiktok_budget: tiktokBudgetEuros,
                tiktok_spend: tiktokSpend,
              });

              toast.success(`${tiktokRemaining.toFixed(2)}€ von TikTok zu Meta verschoben. Meta Budget: ${newMetaBudgetAfterShift.toFixed(2)}€`);
              setShowOverspendModal(false);
              onUpdate();
            } else {
              toast.error(`Fehler beim Aktualisieren des Meta Budgets: ${result?.error || 'Unknown'}`);
            }
          } catch (error) {
            console.error('Error shifting budget:', error);
            toast.error('Fehler beim Verschieben des Budgets');
          } finally {
            setIsShiftingBudget(false);
          }
        };

        return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gray-900 border border-red-500/30 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-white mb-2">⚠️ Budget überprüfen</h2>
            <p className="text-sm text-gray-300 mb-4">
              {project.artist_name} - {project.release_name}
            </p>

            {/* Per-platform budget summary table */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 mb-4 text-sm space-y-2">
              {/* Budget Tool = Meta + TikTok planned */}
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Budget Tool (Meta + TikTok):</span>
                <span className="text-white font-medium">{budgetToolEuros.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€</span>
              </div>

              {/* Budget Monday (falls abweichend) */}
              {mondayDeviation && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Budget Monday.com:</span>
                  <span className="text-yellow-400 font-medium">
                    {mondayBudgetEuros.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                    <span className="text-xs text-gray-500 ml-1">(original)</span>
                  </span>
                </div>
              )}

              {/* Budget Meta: xx / yy (ausgegeben / eingestellt) */}
              {campaignMetrics.meta && campaignMetrics.meta.campaign_count > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">
                    Budget Meta:
                    <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
                      campaignMetrics.meta.status === 'active' ? 'bg-green-500/20 text-green-400'
                      : campaignMetrics.meta.status === 'ended' ? 'bg-gray-500/20 text-gray-400'
                      : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {campaignMetrics.meta.status === 'active'
                        ? `active${metaEndDate ? ` bis ${metaEndDate}` : ''}`
                        : campaignMetrics.meta.status === 'ended'
                        ? `ended${metaEndDate ? ` (${metaEndDate})` : ''}`
                        : campaignMetrics.meta.status}
                    </span>
                  </span>
                  <span className="text-white font-medium">
                    {metaSpend.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€ / {metaBudgetEuros.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                    <span className="text-xs text-gray-500 ml-1">ausg. / eing.</span>
                  </span>
                </div>
              )}

              {/* Budget TikTok: xx / yy (ausgegeben / eingestellt) */}
              {campaignMetrics.tiktok && campaignMetrics.tiktok.campaign_count > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">
                    Budget TikTok:
                    <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
                      campaignMetrics.tiktok.status === 'active' ? 'bg-green-500/20 text-green-400'
                      : campaignMetrics.tiktok.status === 'ended' ? 'bg-gray-500/20 text-gray-400'
                      : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {campaignMetrics.tiktok.status === 'active'
                        ? `active${tiktokEndDate ? ` bis ${tiktokEndDate}` : ''}`
                        : campaignMetrics.tiktok.status === 'ended'
                        ? `ended${tiktokEndDate ? ` (${tiktokEndDate})` : ''}`
                        : campaignMetrics.tiktok.status}
                    </span>
                  </span>
                  <span className="text-white font-medium">
                    {tiktokSpend.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€ / {tiktokBudgetEuros.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                    <span className="text-xs text-gray-500 ml-1">ausg. / eing.</span>
                  </span>
                </div>
              )}

              {/* Divider */}
              <div className="border-t border-gray-700 my-1"></div>

              {/* Budget gesamt: xx / yy geplant? */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300 font-medium">Budget gesamt:</span>
                <span className={`font-semibold ${overspendVsProject ? 'text-red-400' : 'text-white'}`}>
                  {spend.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€ / {budgetToolEuros.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                  <span className="text-xs text-gray-500 ml-1">ausg. / geplant</span>
                </span>
              </div>
            </div>

            {/* Shift TikTok remaining budget to Meta */}
            {canShiftTiktokToMeta && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4 text-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-orange-400 font-medium">TikTok Rest-Budget:</span>
                  <span className="text-orange-400 font-semibold">{tiktokRemaining.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€</span>
                </div>
                <p className="text-xs text-gray-400 mb-3">
                  TikTok Kampagne ist beendet mit {tiktokRemaining.toFixed(2)}€ ungenutztem Budget.
                  Auf Meta verschieben? Meta Budget wird von {metaBudgetEuros.toFixed(2)}€ auf{' '}
                  <strong className="text-white">{newMetaBudgetAfterShift.toFixed(2)}€</strong> erhöht.
                </p>
                <button
                  onClick={handleShiftTiktokToMeta}
                  disabled={isShiftingBudget}
                  className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {isShiftingBudget ? 'Verschiebe...' : `${tiktokRemaining.toFixed(2)}€ zu Meta verschieben`}
                </button>
              </div>
            )}

            {/* Warnings */}
            {(budgetMismatch || lexofficeMismatch || mondayDeviation) && (
              <div className="space-y-2 mb-4">
                {mondayDeviation && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 text-xs text-yellow-400/80">
                    ⚠️ Tool-Budget weicht vom Monday.com Budget ab ({(projectBudgetEuros - mondayBudgetEuros).toLocaleString('de-DE', { minimumFractionDigits: 2 })}€).
                  </div>
                )}
                {budgetMismatch && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 text-xs text-yellow-400/80">
                    ⚠️ Summe der Plattform-Budgets ({totalPlatformBudget.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€) weicht vom Projekt-Budget ({projectBudgetEuros.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€) ab.
                    Bitte Kampagnen-Budgets in Meta/TikTok prüfen.
                  </div>
                )}
                {lexofficeData && lexofficeData.media_budget_net > 0 && (
                  <div className={`border rounded-lg p-2 text-xs ${lexofficeMismatch ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400/80' : 'bg-gray-800/50 border-gray-700 text-gray-400'}`}>
                    <div className="flex justify-between">
                      <span>Invoziert (Lexoffice, Media Budget):</span>
                      <span className="font-medium">{lexofficeData.media_budget_net.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€</span>
                    </div>
                    {lexofficeData.voucher_number && (
                      <div className="text-gray-500 mt-0.5">Rechnung: {lexofficeData.voucher_number} ({lexofficeData.voucher_status || 'unknown'})</div>
                    )}
                    {lexofficeMismatch && (
                      <div className="mt-1">⚠️ Weicht vom Projekt-Budget ab — bitte Rechnung in Lexoffice prüfen.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Only show budget input when there's a real overspend vs project budget */}
            {overspendVsProject ? (
              <>
                <p className="text-sm text-gray-400 mb-3">
                  Die Ausgaben überschreiten das Projekt-Budget. Soll das Budget aktualisiert werden?
                </p>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="number"
                    value={newBudgetInput}
                    onChange={(e) => setNewBudgetInput(e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Neues Budget (€)"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0000FE]"
                  />
                  <span className="text-gray-400 text-sm">€</span>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400 mb-3">
                {budgetMismatch
                  ? 'Das Projekt-Budget stimmt mit den Ausgaben überein. Bitte die Kampagnen-Budgets in Meta/TikTok prüfen.'
                  : lexofficeMismatch
                  ? 'Das Projekt-Budget stimmt mit den Ausgaben überein. Bitte die Rechnung in Lexoffice prüfen.'
                  : 'Bitte die Werte oben prüfen und bestätigen.'}
              </p>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={acknowledgeOverspend}
                disabled={isUpdating}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
              >
                Ignorieren
              </button>
              {overspendVsProject && (
                <button
                  onClick={handleSaveNewBudget}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-[#0000FE] hover:bg-[#0000dd] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {isUpdating ? 'Speichern...' : 'Neues Budget speichern'}
                </button>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {showReportSuggestModal && suggestedReports.length > 0 && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gray-900 border border-blue-500/30 rounded-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-white mb-2">📊 Report gefunden</h2>
            <p className="text-sm text-gray-300 mb-4">
              Für <strong>{project.artist_name} - {project.release_name}</strong> ist noch kein Report verlinkt,
              aber es {suggestedReports.length === 1 ? 'wurde ein passender Report' : `wurden ${suggestedReports.length} passende Reports`} gefunden:
            </p>

            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {suggestedReports.map(report => (
                <button
                  key={report.id}
                  onClick={() => handleLinkSuggestedReport(report)}
                  disabled={linkingReportId !== null}
                  className="w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-white transition-colors disabled:opacity-50"
                >
                  <div className="font-medium flex items-center justify-between">
                    <span className="truncate">{report.name}</span>
                    <span className="text-blue-400 text-xs flex-shrink-0 ml-2">
                      {linkingReportId === report.id ? 'Verlinke...' : '+ Verlinken'}
                    </span>
                  </div>
                  {report.share_url && (
                    <div className="text-xs text-gray-400 truncate">{report.share_url}</div>
                  )}
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={dismissReportSuggestion}
                disabled={linkingReportId !== null}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
              >
                Nicht verlinken
              </button>
            </div>
          </div>
        </div>
      )}

      <div 
        className={`bg-gray-900 border rounded-lg p-4 hover:border-gray-700 transition-all group cursor-pointer ${
          isDragging ? 'opacity-50 scale-95' : ''
        } ${isDragOver ? 'border-purple-500 border-2 bg-purple-500/10' : ''
        } ${!isDragOver && isContentView && (project.pending_video_count || 0) > 0 ? 'border-red-500 border-2' : ''
        } ${!isDragOver && !isContentView && isThisWeekRelease() ? 'border-red-500 border-2' : ''
        } ${!isDragOver && !(isContentView && (project.pending_video_count || 0) > 0) && !((!isContentView) && isThisWeekRelease()) ? 'border-gray-800' : ''}`}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleCardDragOver}
        onDragLeave={handleCardDragLeave}
        onDrop={handleCardDrop}
        onClick={(e) => {
          // Don't open modal if clicking a button or interactive element
          if ((e.target as HTMLElement).closest('button, input, select, a')) return;
          setShowEditModal(true);
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-1">
              <User className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <h4 className="text-sm font-semibold text-white break-words">
                {(() => {
                  const projectName = project.project_name;
                  // If the name contains }}, it's likely followed by JSON content
                  if (projectName && projectName.includes('}}')) {
                    const titlePart = projectName.split('}}')[0];
                    return titlePart || projectName;
                  }
                  return projectName || `${project.artist_name} - ${project.release_name}`;
                })()}
              </h4>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-gray-800 rounded transition-opacity"
              title={isExpanded ? 'Einklappen' : 'Ausklappen'}
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            <button
              onClick={() => setShowEditModal(true)}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-800 rounded"
            >
              <Edit className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Red Alert: This week's release with 0 ad creatives */}
        {!isContentView && isThisWeekRelease() && (project.ad_creative_count || 0) === 0 && (
          <div className="mb-2">
            <span
              onClick={() => navigate(`/ad-creatives?artist=${project.artist_id}`)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 cursor-pointer hover:bg-red-500/30 transition-colors"
            >
              ⚠️ 0 Ad Creatives
            </span>
          </div>
        )}

        {/* Collapsed View - Release Date and Progress */}
        {!isExpanded && (
          <div className="space-y-2">
            {/* For content view running campaigns: show campaign end + bar instead of release/posting dates */}
            {isContentView && ['active_campaign', 'follow_up', 'completed'].includes(project.project_status) && project.campaign_end_date ? (() => {
              const start = project.posting_start_date ? new Date(project.posting_start_date) : project.release_date ? new Date(project.release_date) : null;
              const end = new Date(project.campaign_end_date);
              const now = new Date();
              if (!start) return null;
              const totalDays = Math.max(differenceInDays(end, start), 1);
              const elapsedDays = differenceInDays(now, start);
              const pct = Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100);
              const daysLeft = differenceInDays(end, now);
              const endLabel = daysLeft < 0
                ? `seit ${Math.abs(daysLeft)} Tagen beendet`
                : daysLeft === 0
                  ? 'endet heute'
                  : daysLeft <= 7
                    ? `noch ${daysLeft} Tag${daysLeft > 1 ? 'e' : ''}`
                    : format(end, 'dd.MM.', { locale: de });
              return (
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                    <span className="text-gray-500">Kampagnen Ende:</span>
                    <span className={daysLeft < 0 ? 'text-red-400' : daysLeft <= 3 ? 'text-yellow-400' : 'text-gray-300'}>{endLabel}</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${daysLeft < 0 ? 'bg-red-500' : daysLeft <= 3 ? 'bg-yellow-500' : 'bg-blue-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })() : (
              <>
                {project.release_date && (
                  <div className="text-xs text-gray-400">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Release:</span>
                      <span className={getDateColor(project.release_date)}>
                        {formatDate(project.release_date)}
                      </span>
                    </div>
                  </div>
                )}
                {isContentView && project.posting_start_date && (
                  <div className="text-xs text-gray-400">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Posting Start:</span>
                      <span className={getDateColor(project.posting_start_date)}>
                        {formatDate(project.posting_start_date)}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Progress Bar for projects with budget */}
            {!isContentView && ['active_campaign', 'completed', 'follow_up'].includes(project.project_status) && project.ad_budget > 0 && (
              <div>
                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                  <div className="flex items-center gap-1">
                    <span>Budget Progress</span>
                    {campaignMetrics && (
                      <div title="Reporting data linked">
                        <Link className="w-3 h-3 text-green-500" />
                      </div>
                    )}
                  </div>
                  <span>
                    {campaignMetrics ? (
                      <>
                        {campaignMetrics.spend.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€ / {((project.ad_budget || 0) / 100).toLocaleString('de-DE')}€
                      </>
                    ) : (
                      <>{((project.ad_budget || 0) / 100).toLocaleString('de-DE')}€</>
                    )}
                  </span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      campaignMetrics && campaignMetrics.spend > (project.ad_budget / 100)
                        ? 'bg-red-500'
                        : 'bg-green-500'
                    }`}
                    style={{ 
                      width: campaignMetrics && project.ad_budget > 0
                        ? `${Math.min((campaignMetrics.spend / (project.ad_budget / 100)) * 100, 100)}%`
                        : '0%'
                    }}
                    title={campaignMetrics 
                      ? `Spent: ${campaignMetrics.spend.toLocaleString('de-DE')}€ | Impressions: ${campaignMetrics.impressions.toLocaleString('de-DE')} | Clicks: ${campaignMetrics.clicks.toLocaleString('de-DE')}`
                      : 'Loading campaign data...'
                    }
                  />
                </div>
                
              </div>
            )}
          </div>
        )}

        {/* Expanded View */}
        {isExpanded && (
          <>
        {/* Package Badge */}
        <div className="mb-3 relative package-menu-container">
          <button
            onClick={() => !isContentView && setShowPackageMenu(!showPackageMenu)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getPackageColor(project.service_package)} ${isContentView ? '' : 'hover:opacity-80 transition-opacity cursor-pointer'}`}
          >
            <Package className="w-3 h-3" />
            {getPackageLabel(project.service_package)}
          </button>
          
          {showPackageMenu && (
            <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-10 min-w-[140px]">
              <button
                onClick={() => handlePackageChange('full')}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-700 transition-colors flex items-center gap-2 rounded-t-lg"
              >
                <Package className="w-3 h-3 text-blue-400" />
                <span className="text-white">Full</span>
              </button>
              <button
                onClick={() => handlePackageChange('ad_only')}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <Package className="w-3 h-3 text-purple-400" />
                <span className="text-white">Ads Only</span>
              </button>
              <button
                onClick={() => handlePackageChange('content_only')}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-700 transition-colors flex items-center gap-2 rounded-b-lg"
              >
                <Package className="w-3 h-3 text-green-400" />
                <span className="text-white">Content Only</span>
              </button>
            </div>
          )}
        </div>

        {/* Onboarding Progress (only show in onboarding status) */}
        {/* Content view onboarding: Google Sheet + Erste 4 Videos */}
        {project.project_status === 'onboarding' && isContentView && (
          <div className="mb-3 space-y-1">
            {/* Masterclass steps temporarily hidden — not active right now */}
            {/* <OnboardingStep completed={project.masterclass_login_created} label="Masterclass Login" onClick={() => handleToggleStep('masterclass_login_created', project.masterclass_login_created)} /> */}
            {/* <OnboardingStep completed={project.masterclass_watched} label="Masterclass geschaut" onClick={() => handleToggleStep('masterclass_watched', project.masterclass_watched)} /> */}
            <OnboardingStep completed={project.release_sheet_completed || !!project.notion_page_url} label="Google Sheet" onClick={() => handleToggleStep('release_sheet_completed', project.release_sheet_completed)} />
            <OnboardingStep completed={project.first_four_videos_uploaded} label="Erste 4 Videos" onClick={() => handleToggleStep('first_four_videos_uploaded', project.first_four_videos_uploaded)} />
          </div>
        )}
        {/* Ads view onboarding: only Meta Access */}
        {project.project_status === 'onboarding' && isAdsView && (
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <OnboardingStep completed={project.meta_access_granted} label="Meta Access" onClick={() => handleToggleStep('meta_access_granted', project.meta_access_granted)} />
              {!project.meta_access_granted && (
                <button
                  onClick={handleCheckMetaAccess}
                  disabled={isCheckingMetaAccess}
                  className="text-xs px-2 py-0.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded transition-colors disabled:opacity-50 whitespace-nowrap"
                  title="Meta API prüfen ob FB Page / IG Account verknüpft ist"
                >
                  {isCheckingMetaAccess ? 'Prüfe...' : 'Prüfen'}
                </button>
              )}
            </div>
          </div>
        )}
        {project.project_status === 'onboarding' && !isContentView && !isAdsView && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
              <span>Onboarding</span>
              <span>{progress.completed}/{progress.total}</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1.5">
              <div
                className="bg-primary-500 h-1.5 rounded-full transition-all"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2">
                  <OnboardingStep completed={project.meta_access_granted} label="Meta Access" onClick={() => handleToggleStep('meta_access_granted', project.meta_access_granted)} />
                  {!project.meta_access_granted && (
                    <button
                      onClick={handleCheckMetaAccess}
                      disabled={isCheckingMetaAccess}
                      className="text-xs px-2 py-0.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded transition-colors disabled:opacity-50 whitespace-nowrap"
                      title="Meta API prüfen ob FB Page / IG Account verknüpft ist"
                    >
                      {isCheckingMetaAccess ? 'Prüfe...' : 'Prüfen'}
                    </button>
                  )}
                </div>
              </div>
          </div>
        )}

        {/* Content Production Steps — hidden in ads view */}
        {project.project_status === 'content_production' && project.service_package !== 'ad_only' && !isAdsView && (
          <div className="mb-3 space-y-1">
            <OnboardingStep completed={project.content_call_scheduled} label="Content Call geplant" onClick={() => handleToggleStep('content_call_scheduled', project.content_call_scheduled)} />
            <OnboardingStep completed={project.content_call_prepared} label="Content Call vorbereitet" onClick={() => handleToggleStep('content_call_prepared', project.content_call_prepared)} />
            <OnboardingStep completed={project.content_call_completed} label="Content Call abgehalten" onClick={() => handleToggleStep('content_call_completed', project.content_call_completed)} />
          </div>
        )}

        {/* Timeline Dates as Tags */}
        {(project.posting_start_date || project.release_date) && (
          <div className="mb-3 flex flex-wrap gap-2">
            {project.posting_start_date && !isAdsView && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                Posting Start: {formatDate(project.posting_start_date)}
              </span>
            )}
            {project.release_date && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                Release: {formatDate(project.release_date)}
              </span>
            )}
          </div>
        )}

        {/* Campaign timeline bar for content view (expanded) */}
        {isContentView && ['active_campaign', 'follow_up', 'completed'].includes(project.project_status) && project.campaign_end_date && (() => {
          const start = project.posting_start_date ? new Date(project.posting_start_date) : project.release_date ? new Date(project.release_date) : null;
          const end = new Date(project.campaign_end_date);
          const now = new Date();
          if (!start) return null;
          const totalDays = Math.max(differenceInDays(end, start), 1);
          const elapsedDays = differenceInDays(now, start);
          const pct = Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100);
          const daysLeft = differenceInDays(end, now);
          const endLabel = daysLeft < 0
            ? `seit ${Math.abs(daysLeft)} Tagen beendet`
            : daysLeft === 0
              ? 'endet heute'
              : daysLeft <= 7
                ? `noch ${daysLeft} Tag${daysLeft > 1 ? 'e' : ''}`
                : `bis ${format(end, 'dd.MM.', { locale: de })}`;
          return (
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                <span>Kampagne</span>
                <span>{endLabel}</span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${daysLeft < 0 ? 'bg-red-500' : daysLeft <= 3 ? 'bg-yellow-500' : 'bg-blue-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })()}

        {/* Stats */}
        {isAdsView && !['active_campaign', 'completed', 'follow_up'].includes(project.project_status) ? (
          <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
            <div className="bg-gray-800/50 rounded p-2">
              <div className="text-gray-500">Budget</div>
              <div className="text-white font-medium">
                {project.ad_budget ? `${(project.ad_budget / 100).toLocaleString('de-DE', { maximumFractionDigits: 0 })}€` : '—'}
              </div>
            </div>
            <div className="bg-gray-800/50 rounded p-2">
              <div className="text-gray-500">Release</div>
              <div className="text-white font-medium">{formatDate(project.release_date)}</div>
            </div>
          </div>
        ) : isContentView ? (
          <div className="flex items-center gap-2 mb-3 text-xs cursor-pointer hover:bg-gray-800/50 rounded p-1 transition-colors" onClick={() => onCheckPostedVideos?.(project.artist_id, project.artist_name)}>
            <Video className="w-4 h-4 text-gray-400" />
            <span className="text-white font-medium">{project.video_count} Videos</span>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2 mb-3 text-xs">
            <div className="bg-gray-800/50 rounded p-2 text-center cursor-pointer hover:bg-gray-700/50 transition-colors" onClick={() => onCheckPostedVideos?.(project.artist_id, project.artist_name)}>
              <Video className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <div className="text-white font-medium">{project.video_count}</div>
              <div className="text-gray-500">Videos</div>
            </div>
            <div className="bg-gray-800/50 rounded p-2 text-center">
              <Instagram className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <div className="text-white font-medium">{project.ad_creative_instagram_count || 0}</div>
              <div className="text-gray-500">IG</div>
            </div>
            <div className="bg-gray-800/50 rounded p-2 text-center">
              <Music className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <div className="text-white font-medium">{project.ad_creative_tiktok_count || 0}</div>
              <div className="text-gray-500">TikTok</div>
            </div>
            <div className="bg-gray-800/50 rounded p-2 text-center">
              <FileText className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <div className="text-white font-medium">{project.ad_creative_files_count || 0}</div>
              <div className="text-gray-500">Files</div>
            </div>
          </div>
        )}


        {/* Notes Preview */}
        {project.notes && (
          <div className="mt-3 pt-3 border-t border-gray-800">
            <p className="text-xs text-gray-500 line-clamp-2">{project.notes}</p>
          </div>
        )}
          </>
        )}
      </div>
    </>
  );
};

const OnboardingStep: React.FC<{ completed: boolean; label: string; onClick?: () => void }> = ({ completed, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 text-xs hover:bg-gray-800/50 rounded px-1 py-0.5 -mx-1 transition-colors w-full text-left cursor-pointer"
  >
    {completed ? (
      <CheckCircle2 className="w-3 h-3 text-green-400 flex-shrink-0" />
    ) : (
      <Circle className="w-3 h-3 text-gray-600 flex-shrink-0" />
    )}
    <span className={completed ? 'text-gray-400 line-through' : 'text-gray-300'}>
      {label}
    </span>
  </button>
);
